import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PayerWizardProvider, usePayerWizardContext } from '@/components/payer-wizard/PayerWizardContext';
import { PayerWizardStepIndicator } from '@/components/payer-wizard/PayerWizardStepIndicator';
import { PayerStep1Setup } from '@/components/payer-wizard/steps/PayerStep1Setup';
import { PayerStep2Plans } from '@/components/payer-wizard/steps/PayerStep2Plans';
import { PayerStep3RuleReview } from '@/components/payer-wizard/steps/PayerStep3RuleReview';
import { PayerStep4LaneConfig } from '@/components/payer-wizard/steps/PayerStep4LaneConfig';
import { PayerStep5Activate } from '@/components/payer-wizard/steps/PayerStep5Activate';
import { SetupCompletionChecklist } from '@/components/payer-wizard/SetupCompletionChecklist';
import { generateChecklist } from '@/lib/payer-wizard/checklistUtils';
import { Button } from '@/components/ui/button';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { ArrowLeft } from 'lucide-react';

function PayerWizardContent() {
  const { progress, ruleReview, initializeWizard } = usePayerWizardContext();
  
  useEffect(() => {
    if (!progress) {
      initializeWizard();
    }
  }, [progress, initializeWizard]);
  
  if (!progress) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  const completedSteps = [
    progress.step1_complete,
    progress.step2_complete,
    progress.step3_complete,
    progress.step4_complete,
    progress.step5_complete,
  ];
  
  const checklist = generateChecklist(progress, ruleReview);
  
  // Show checklist after Step 2 (once plans exist)
  const showChecklist = progress.current_step >= 3;
  
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/payer-plans">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Payer & Ruleset Setup Wizard</h1>
          <p className="text-muted-foreground">
            Add new payers and configure plans with proper rulesets and submission lanes
          </p>
        </div>
      </div>
      
      <PayerWizardStepIndicator 
        currentStep={progress.current_step} 
        completedSteps={completedSteps}
      />
      
      {/* Top-level checklist (visible from Step 3 onwards) */}
      {showChecklist && (
        <SetupCompletionChecklist items={checklist.items} compact />
      )}
      
      {progress.current_step === 1 && <PayerStep1Setup />}
      {progress.current_step === 2 && <PayerStep2Plans />}
      {progress.current_step === 3 && <PayerStep3RuleReview />}
      {progress.current_step === 4 && <PayerStep4LaneConfig />}
      {progress.current_step === 5 && <PayerStep5Activate />}
    </div>
  );
}

export default function PayerSetupWizardPage() {
  return (
    <OpsLayout>
      <div className="w-full max-w-4xl mx-auto">
        <PayerWizardProvider>
          <PayerWizardContent />
        </PayerWizardProvider>
      </div>
    </OpsLayout>
  );
}
